import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/ui/sonner';
import { ArrowLeft } from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    const result = await signup(name, email, password);
    
    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0d1b2a]">
      <style>{`
        @keyframes float-0 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -40px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.95); }
          75% { transform: translate(40px, 30px) scale(1.05); }
        }
        @keyframes float-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-40px, 30px) scale(1.08); }
          50% { transform: translate(30px, -30px) scale(0.92); }
          75% { transform: translate(-20px, -40px) scale(1.12); }
        }
        @keyframes glow {
          0% { opacity: 0.5; }
          100% { opacity: 0.9; }
        }
      `}</style>

      {/* Animated orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute rounded-full"
          style={{
            width: '600px', height: '600px', left: '-10%', top: '5%',
            background: 'radial-gradient(circle, rgba(61, 106, 159, 0.8) 0%, rgba(45, 74, 111, 0.4) 40%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'float-0 12s ease-in-out infinite, glow 4s ease-in-out infinite alternate',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '550px', height: '550px', right: '-5%', bottom: '10%',
            background: 'radial-gradient(circle, rgba(77, 122, 191, 0.75) 0%, rgba(45, 90, 140, 0.35) 40%, transparent 70%)',
            filter: 'blur(45px)',
            animation: 'float-1 14s ease-in-out infinite, glow 5s ease-in-out infinite alternate',
            animationDelay: '1s',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '500px', height: '500px', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(61, 106, 159, 0.5) 0%, transparent 60%)',
            filter: 'blur(60px)',
            animation: 'glow 5s ease-in-out infinite alternate',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center p-4 w-full max-w-md">
        {/* Back to landing */}
        <Link
          to="/"
          className="self-start mb-4 flex items-center gap-1.5 text-[#7a9cc6] hover:text-white text-xs transition-colors"
          data-testid="back-to-landing-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </Link>

        <div 
          className="w-full rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 0 30px 6px rgba(61, 106, 159, 0.4), 0 0 60px 12px rgba(61, 106, 159, 0.2)' }}
        >
          {/* Logo Block */}
          <div className="bg-white py-3 px-4 flex items-center justify-center">
            <img 
              src="/logos/logo-navy-wolf.png" 
              alt="Strategy Wolves" 
              className="w-full max-w-[180px] h-auto object-contain"
            />
          </div>
          
          {/* Glowing separator */}
          <div 
            className="h-px w-full"
            style={{ 
              background: 'linear-gradient(90deg, transparent 0%, rgba(61, 106, 159, 0.5) 20%, rgba(61, 106, 159, 0.8) 50%, rgba(61, 106, 159, 0.5) 80%, transparent 100%)',
              boxShadow: '0 2px 10px 0 rgba(61, 106, 159, 0.4)'
            }}
          />
          
          {/* Sign Up Block */}
          <div className="bg-[#132438] px-6 py-4">
            <div className="text-center mb-3">
              <h2 
                className="text-lg font-bold text-white"
                style={{ textShadow: '0 0 8px rgba(255,255,255,0.4), 0 0 16px rgba(255,255,255,0.2)' }}
              >
                Strategy Wolves
              </h2>
              <p className="text-[#7a9cc6] text-xs mt-0.5">Create your account</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="signup-form">
              <div>
                <Label className="text-[#a3c4e8] text-xs">Full Name</Label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="name-input"
                  className="mt-1 h-9 bg-[#0d1b2a]/60 border-[#1e3a5f]/50 text-white placeholder:text-[#5a7a9c] focus:border-[#3d6a9f] focus:ring-[#3d6a9f]/20"
                />
              </div>
              <div>
                <Label className="text-[#a3c4e8] text-xs">Email</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="mt-1 h-9 bg-[#0d1b2a]/60 border-[#1e3a5f]/50 text-white placeholder:text-[#5a7a9c] focus:border-[#3d6a9f] focus:ring-[#3d6a9f]/20"
                />
              </div>
              <div>
                <Label className="text-[#a3c4e8] text-xs">Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="mt-1 h-9 bg-[#0d1b2a]/60 border-[#1e3a5f]/50 text-white placeholder:text-[#5a7a9c] focus:border-[#3d6a9f] focus:ring-[#3d6a9f]/20"
                />
              </div>
              <div>
                <Label className="text-[#a3c4e8] text-xs">Confirm Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className="mt-1 h-9 bg-[#0d1b2a]/60 border-[#1e3a5f]/50 text-white placeholder:text-[#5a7a9c] focus:border-[#3d6a9f] focus:ring-[#3d6a9f]/20"
                />
              </div>
              
              <Button
                type="submit"
                data-testid="signup-submit-btn"
                className="w-full h-9 bg-gradient-to-r from-[#1e3a5f] via-[#2d4a6f] to-[#3d5a7f] hover:from-[#2d4a6f] hover:to-[#4d6a8f] text-white font-semibold shadow-lg shadow-[#1e3a5f]/30 transition-all"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating account...
                  </span>
                ) : 'Sign Up'}
              </Button>
            </form>
            
            <div className="mt-3 text-center text-xs text-[#7a9cc6]">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#5a9fd4] font-semibold hover:text-[#7ab8e8] transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>

        <p className="mt-6 text-[#5a7a9c] text-xs">
          © 2026 Strategy Wolves. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Signup;
